import styled, { keyframes } from "styled-components";

const shimmer = keyframes`
	0% { background-position: -200px 0; }
	100% { background-position: 200px 0; }
`;

export const SkeletonRow = styled.div`
	height: 66px;
	width: 100%;
	border-radius: 12px;
	background: linear-gradient(90deg, #f3f5f8 25%, #e4e8ef 50%, #f3f5f8 75%);
	background-size: 200% 100%;
	animation: ${shimmer} 1.5s infinite linear;
	margin-bottom: 10px;
`;

export const ModalPaper = styled.div`
	background-color: #ffffff;
	border: 0;
	border-radius: 18px;
	width: min(540px, calc(100% - 24px));
	height: min(580px, calc(100vh - 24px));
	box-shadow: 0 22px 70px rgba(0, 14, 75, 0.3);
	overflow: hidden;
	display: flex;
	flex-direction: column;
	outline: none;
	position: relative;
`;

export const ModalTop = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 18px;
	align-items: center;
	padding: 21px 22px;
	border-bottom: 1px solid #edf0f4;
`;

export const ModalTitle = styled.h2`
	font-family: "Inter-Bold", sans-serif;
	font-size: 22px;
	font-weight: 700;
	margin: 0;
	color: #3d4259;
`;

export const CloseButton = styled.button`
	border: 0;
	background: transparent;
	width: 32px;
	height: 32px;
	font-size: 24px;
	line-height: 1;
	color: #000e4b;
	display: grid;
	place-items: center;
	border-radius: 50%;
	cursor: pointer;
	transition: background-color 0.2s ease;

	&:hover {
		background: #f3f5f8;
	}

	&:focus-visible {
		outline: 2px solid #00d084;
		outline-offset: 1px;
	}
`;

export const ModalDescription = styled.div`
	padding: 20px 22px 11px;

	p {
		color: #797d8d;
		font-family: "Inter-Regular", sans-serif;
		font-size: 12px;
		line-height: 1.5;
		margin: 0;
	}
`;

export const ModalContent = styled.div`
	padding: 0 22px 22px;
	overflow-y: auto;
	flex: 1;
	display: flex;
	flex-direction: column;

	/* Chrome, Safari e Edge */
	&::-webkit-scrollbar {
		width: 6px;
	}
	&::-webkit-scrollbar-thumb {
		background-color: #e4e7ed;
		border-radius: 10px;
	}
	&::-webkit-scrollbar-track {
		background: transparent;
	}

	/* Firefox */
	scrollbar-width: thin;
	scrollbar-color: #e4e7ed transparent;
`;

export const PatientSearch = styled.div`
	height: 44px;
	border-bottom: 1px solid #e4e7ed;
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 16px;

	svg {
		width: 18px;
		height: 18px;
		color: #000e4b;
	}

	input {
		border: 0;
		outline: 0;
		width: 100%;
		color: #000e4b;
		font-family: "NotoSans-Regular", sans-serif;
		font-size: 13px;
		background: transparent;

		&::placeholder {
			color: #a3abc0;
		}
	}
`;

export const PatientList = styled.div`
	display: grid;
	gap: 10px;
`;

export const PatientItem = styled.article`
	border: 1px solid #e2e5eb;
	border-radius: 12px;
	background: #ffffff;
	padding: 11px 12px;
	transition: border-color 0.2s ease;
`;

export const PatientSummary = styled.div`
	display: grid;
	grid-template-columns: auto 1fr auto;
	align-items: center;
	gap: 12px;
`;

export const ListAvatar = styled.span<{ warm?: boolean }>`
	width: 42px;
	height: 42px;
	border-radius: 50%;
	display: grid;
	place-items: center;
	font-family: "NotoSans-Bold", sans-serif;
	color: #ffffff;
	font-size: 11px;
	font-weight: 800;
	border: 1px solid #cfd5df;

	background: ${({ warm }) =>
		warm
			? "linear-gradient(145deg, #ead8cb, #8e5a44)"
			: "linear-gradient(145deg, #c7dbe7, #477889)"};
	overflow: hidden;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`;

export const PatientInfo = styled.div`
	display: flex;
	flex-direction: column;
`;

export const ListName = styled.div`
	font-family: "Inter-Bold", sans-serif;
	font-size: 13px;
	font-weight: 700;
	color: #3d4259;
`;

export const OpenCopilotButton = styled.button`
	border-radius: 8px;
	padding: 9px 16px;
	font-size: 11px;
	font-weight: 700;
	font-family: "NotoSans-Bold", sans-serif;
	cursor: pointer;
	border: 1px solid #cfd5df;
	background: #ffffff;
	color: #000e4b;
	display: flex;
	align-items: center;
	gap: 6px;
	white-space: nowrap;
	transition: all 0.2s ease;

	&:hover {
		background: #f8fafc;
		border-color: #a3abc0;
	}

	&:focus-visible {
		outline: 2px solid #00d084;
		outline-offset: 1px;
	}

	svg {
		width: 12px;
		height: 12px;
	}
`;

export const LoadMoreButton = styled.button`
	display: block;
	margin: 26px auto 4px;
	border: 0;
	border-bottom: 1px solid currentColor;
	background: transparent;
	color: #009973;
	font-family: "NotoSans-Bold", sans-serif;
	font-size: 11px;
	font-weight: 700;
	padding: 0;
	cursor: pointer;
	transition: color 0.2s ease;

	&:hover {
		color: #005a48;
	}

	&:focus-visible {
		outline: 2px solid #00d084;
		outline-offset: 3px;
	}
`;

const rotate = keyframes`
	from { transform: rotate(0deg); }
	to { transform: rotate(360deg); }
`;

export const LoadMoreSpinnerContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	margin: 26px auto 4px;
	height: 16px;
`;

export const MiniSpinner = styled.div`
	width: 14px;
	height: 14px;
	border: 2px solid rgba(0, 153, 115, 0.2);
	border-top-color: #009973;
	border-radius: 50%;
	animation: ${rotate} 0.8s infinite linear;
`;

export const NoResults = styled.div`
	font-family: "NotoSans-Regular", sans-serif;
	text-align: center;
	padding: 28px 10px;
	color: #797d8d;
	font-size: 13px;
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	gap: 12px;

	button {
		border: 0;
		background: transparent;
		color: #009973;
		font: inherit;
		font-weight: 700;
		text-decoration: underline;
		cursor: pointer;
	}
`;
